// src/pages/FundWallet.jsx
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "../api/api";
import { toast } from "react-toastify";
import FundingBonusBanner from "../components/FundingBonusBanner";
import {
  ArrowLeft, Copy, RefreshCw, CheckCircle2,
  AlertTriangle, Banknote, BadgeCheck, UploadCloud, Zap,
} from "lucide-react";

export default function FundWallet() {
  const navigate = useNavigate();
  const [mode, setMode] = useState("choice");
  const [checking, setChecking] = useState(true);
  const [existingAccount, setExistingAccount] = useState(null);

  // Quietly check if the user already has a dedicated account.
  // GET /wallet only reads, it never creates an account.
  useEffect(() => {
    let cancelled = false;
    api.get("/wallet")
      .then((res) => {
        if (cancelled) return;
        const va = res.data?.virtualAccount;
        if (va && va.account_number && va.account_number !== "N/A") {
          setExistingAccount(va);
          setMode("virtual");
        }
      })
      .catch(() => {})
      .finally(() => { if (!cancelled) setChecking(false); });
    return () => { cancelled = true; };
  }, []);

  const hasAccount = !!existingAccount;

  const handleBack = () => {
    if (mode === "choice") return navigate(-1);
    if (hasAccount && mode === "manual") return setMode("virtual");
    if (hasAccount && mode === "virtual") return navigate(-1);
    setMode("choice");
  };

  const backLabel =
    mode === "choice" ? "Back"
    : hasAccount && mode === "manual" ? "Back to my account"
    : hasAccount && mode === "virtual" ? "Back"
    : "Choose a different method";

  if (checking) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-[#0e0e10] flex items-center justify-center">
        <div className="w-10 h-10 rounded-full border-2 border-purple-500 border-t-transparent animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-[#0e0e10]">
      <div className="max-w-md mx-auto px-4 py-6">
        <div className="flex items-center justify-between mb-6">
          <button
            onClick={handleBack}
            className="flex items-center gap-1.5 text-xs text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition"
          >
            <ArrowLeft size={13} />
            {backLabel}
          </button>
        </div>

        <div className="mb-5">
          <h1 className="text-xl font-semibold text-gray-900 dark:text-white tracking-tight">Fund Wallet</h1>
          <p className="text-xs text-gray-400 mt-0.5">
            {mode === "choice" && "Choose how you'd like to fund your wallet"}
            {mode === "virtual" && "Transfer to your dedicated account below"}
            {mode === "manual" && "Transfer to our account, then submit your proof"}
          </p>
        </div>

        <FundingBonusBanner />
        {mode === "choice" && <ChoiceScreen onSelect={setMode} />}
        {mode === "virtual" && (
          <VirtualAccountScreen
            initialAccount={existingAccount}
            onFallback={() => setMode("manual")}
          />
        )}
        {mode === "manual" && <ManualFundScreen />}
      </div>
    </div>
  );
}

function ChoiceScreen({ onSelect }) {
  return (
    <div className="space-y-3">
      <button
        onClick={() => onSelect("virtual")}
        className="w-full bg-white dark:bg-[#18181b] rounded-2xl border border-gray-100 dark:border-white/[0.06] shadow-sm p-5 text-left hover:border-purple-300 dark:hover:border-purple-700 transition"
      >
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 bg-purple-50 dark:bg-purple-900/20 rounded-xl flex items-center justify-center shrink-0">
            <Zap size={18} className="text-purple-600 dark:text-purple-400" />
          </div>
          <div className="flex-1">
            <p className="text-sm font-semibold text-gray-900 dark:text-white">Generate Virtual Account</p>
            <p className="text-xs text-gray-400 mt-1 leading-relaxed">
              Get a dedicated account number instantly. Funds reflect within seconds automatically.
            </p>
          </div>
        </div>
      </button>

      <button
        onClick={() => onSelect("manual")}
        className="w-full bg-white dark:bg-[#18181b] rounded-2xl border border-gray-100 dark:border-white/[0.06] shadow-sm p-5 text-left hover:border-green-300 dark:hover:border-green-700 transition"
      >
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 bg-green-50 dark:bg-green-900/20 rounded-xl flex items-center justify-center shrink-0">
            <Banknote size={18} className="text-green-600 dark:text-green-400" />
          </div>
          <div className="flex-1">
            <p className="text-sm font-semibold text-gray-900 dark:text-white">Fund Manually</p>
            <p className="text-xs text-gray-400 mt-1 leading-relaxed">
              Transfer to our account directly, then upload your receipt. Admin credits your wallet shortly after.
            </p>
          </div>
        </div>
      </button>
    </div>
  );
}

function VirtualAccountScreen({ onFallback, initialAccount }) {
  const [loading, setLoading] = useState(!initialAccount);
  const [generating, setGenerating] = useState(false);
  const [virtualAccount, setVirtualAccount] = useState(initialAccount || null);
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(null);

  const fetchVirtualAccount = async () => {
    try {
      setLoading(true);
      setError("");
      const res = await api.post("/wallet/generate-virtual-account");
      if (res.data.success) {
        setVirtualAccount(res.data.virtualAccount);
      } else {
        setError(res.data.message || "Failed to load account");
      }
    } catch (err) {
      setError(err.response?.data?.message || "Could not connect to server");
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    try {
      setGenerating(true);
      const res = await api.post("/wallet/generate-virtual-account");
      if (res.data.success) {
        setVirtualAccount(res.data.virtualAccount);
        toast.success("Account refreshed");
      } else {
        toast.error(res.data.message || "Failed to refresh");
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Refresh failed");
    } finally {
      setGenerating(false);
    }
  };

  useEffect(() => {
    if (!initialAccount) fetchVirtualAccount();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const copy = (text, field) => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    setCopied(field);
    toast.success("Copied!", { autoClose: 1200 });
    setTimeout(() => setCopied(null), 2000);
  };

  const va = virtualAccount || {};
  const hasAccount = va.account_number && va.account_number !== "N/A";

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-16 gap-3">
        <div className="w-10 h-10 rounded-full border-2 border-purple-500 border-t-transparent animate-spin" />
        <p className="text-sm text-gray-400">Loading account details…</p>
      </div>
    );
  }

  if (error && !virtualAccount) {
    return (
      <div className="bg-white dark:bg-[#18181b] rounded-2xl p-6 text-center shadow-sm border border-gray-100 dark:border-white/[0.06]">
        <div className="w-10 h-10 bg-amber-50 dark:bg-amber-900/20 rounded-xl flex items-center justify-center mx-auto mb-3">
          <AlertTriangle size={18} className="text-amber-500" />
        </div>
        <p className="text-sm font-medium text-gray-900 dark:text-white mb-1">
          We couldn't create a dedicated account for you
        </p>
        <p className="text-xs text-gray-400 mb-2 leading-relaxed">
          This can happen when your email or phone number is already linked to an account with our payment partner, so an automatic account can't be issued.
        </p>
        <p className="text-xs text-gray-500 dark:text-gray-300 mb-4 leading-relaxed">
          No problem. Please use <span className="font-semibold">Manual Funding</span> instead: transfer to our account, upload your receipt, and your wallet will be credited shortly.
        </p>
        <div className="flex flex-col gap-2">
          <button
            onClick={onFallback}
            className="w-full py-2.5 text-sm bg-green-600 hover:bg-green-700 text-white rounded-xl font-medium transition"
          >
            Use Manual Funding
          </button>
          <button
            onClick={fetchVirtualAccount}
            className="w-full py-2.5 text-sm bg-gray-100 dark:bg-white/[0.06] text-gray-600 dark:text-gray-300 rounded-xl font-medium transition hover:bg-gray-200 dark:hover:bg-white/[0.1]"
          >
            Try again
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="flex justify-end mb-3">
        <button
          onClick={handleRefresh}
          disabled={generating}
          className="flex items-center gap-1.5 text-xs text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition disabled:opacity-40"
        >
          <RefreshCw size={12} className={generating ? "animate-spin" : ""} />
          Refresh
        </button>
      </div>

      <div className="bg-white dark:bg-[#18181b] rounded-2xl border border-gray-100 dark:border-white/[0.06] shadow-sm overflow-hidden">
        <div className="h-1 w-full bg-gradient-to-r from-purple-500 via-violet-500 to-purple-400" />
        <div className="p-5">
          <div className="flex items-center gap-2.5 mb-5">
            <div className="w-8 h-8 bg-purple-50 dark:bg-purple-900/20 rounded-lg flex items-center justify-center shrink-0">
              <Banknote size={15} className="text-purple-600 dark:text-purple-400" />
            </div>
            <div>
              <p className="text-xs font-medium text-gray-900 dark:text-white leading-tight">
                {hasAccount ? "Dedicated Account" : "Generating Account…"}
              </p>
              <p className="text-[11px] text-gray-400 leading-tight mt-0.5">NGN</p>
            </div>
            {hasAccount && (
              <span className="ml-auto flex items-center gap-1 text-[10px] font-medium text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-900/20 px-2 py-0.5 rounded-full">
                <BadgeCheck size={10} />
                Active
              </span>
            )}
          </div>

          {hasAccount ? (
            <div className="space-y-2">
              <AccountRow label="Bank" value={va.bank_name || "PaymentPoint Bank"} field="bank" copied={copied} onCopy={copy} />
              <div
                onClick={() => copy(va.account_number, "account")}
                className="group flex items-center justify-between bg-gray-50 dark:bg-white/[0.04] hover:bg-purple-50 dark:hover:bg-purple-900/10 rounded-xl px-4 py-3 cursor-pointer transition"
              >
                <div>
                  <p className="text-[10px] text-gray-400 mb-0.5">Account number</p>
                  <p className="font-mono text-lg font-bold tracking-[0.12em] text-gray-900 dark:text-white leading-none">
                    {va.account_number}
                  </p>
                </div>
                <div className="w-7 h-7 rounded-lg bg-white dark:bg-white/[0.06] border border-gray-200 dark:border-white/[0.08] flex items-center justify-center shrink-0">
                  {copied === "account"
                    ? <CheckCircle2 size={13} className="text-green-500" />
                    : <Copy size={13} className="text-gray-400" />}
                </div>
              </div>
              <AccountRow label="Account name" value={va.account_name || "—"} field="name" copied={copied} onCopy={copy} />
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-8 gap-3">
              <div className="w-8 h-8 rounded-full border-2 border-purple-400 border-t-transparent animate-spin" />
              <p className="text-xs text-gray-400">Setting up your account…</p>
            </div>
          )}
        </div>
      </div>

      <div className="mt-3 bg-amber-50 dark:bg-amber-900/10 border border-amber-100 dark:border-amber-800/30 rounded-xl px-4 py-3">
        <p className="text-[11px] font-medium text-amber-700 dark:text-amber-400 mb-1.5 flex items-center gap-1.5">
          <AlertTriangle size={11} />
          Before you transfer
        </p>
        <ul className="text-[11px] text-amber-600 dark:text-amber-500 space-y-1 leading-relaxed">
          <li>• Use <span className="font-medium">NGN only</span>. Other currencies are rejected</li>
          <li>• Funds reflect <span className="font-medium">within seconds</span> after transfer</li>
          <li>• This account is <span className="font-medium">yours only</span>, safe to save</li>
        </ul>
      </div>

      <button
        onClick={onFallback}
        className="mt-3 w-full flex items-center justify-center gap-2 py-2.5 text-sm bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400 border border-green-200 dark:border-green-800 rounded-xl font-medium transition hover:bg-green-100 dark:hover:bg-green-900/30"
      >
        <Banknote size={15} />
        Fund Manually Instead
      </button>
    </>
  );
}

function AccountRow({ label, value, field, copied, onCopy }) {
  return (
    <div
      onClick={() => onCopy(value, field)}
      className="group flex items-center justify-between bg-gray-50 dark:bg-white/[0.04] hover:bg-purple-50 dark:hover:bg-purple-900/10 rounded-xl px-4 py-2.5 cursor-pointer transition"
    >
      <div>
        <p className="text-[10px] text-gray-400 mb-0.5">{label}</p>
        <p className="text-sm font-medium text-gray-900 dark:text-white leading-none">{value}</p>
      </div>
      <div className="w-6 h-6 rounded-md bg-white dark:bg-white/[0.06] border border-gray-200 dark:border-white/[0.08] flex items-center justify-center shrink-0">
        {copied === field
          ? <CheckCircle2 size={11} className="text-green-500" />
          : <Copy size={11} className="text-gray-400" />}
      </div>
    </div>
  );
}

function ManualFundScreen() {
  const [details, setDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(true);
  const [copied, setCopied] = useState(null);

  const [amount, setAmount] = useState("");
  const [reference, setReference] = useState("");
  const [receipt, setReceipt] = useState(null);
  const [receiptPreview, setReceiptPreview] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    api.get("/wallet/manual-funding-details")
      .then(res => { if (res.data.success) setDetails(res.data.details); })
      .catch(() => toast.error("Failed to load funding account details"))
      .finally(() => setLoadingDetails(false));
  }, []);

  const copy = (text, field) => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    setCopied(field);
    toast.success("Copied!", { autoClose: 1200 });
    setTimeout(() => setCopied(null), 2000);
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!["image/png", "image/jpeg", "image/jpg"].includes(file.type)) {
      toast.error("Only PNG or JPG images are accepted");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image must be under 5MB");
      return;
    }
    setReceipt(file);
    setReceiptPreview(URL.createObjectURL(file));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const amountNum = Number(amount);
    if (!amount || isNaN(amountNum) || amountNum <= 0) {
      toast.error("Enter a valid amount");
      return;
    }
    if (!receipt) {
      toast.error("Please upload your receipt");
      return;
    }

    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append("amount", amountNum);
      formData.append("reference", reference.trim());
      formData.append("receipt", receipt);

      const res = await api.post("/wallet/submit-proof", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (res.data.success) {
        setSubmitted(true);
        toast.success("Submitted! Admin has been notified.");
      } else {
        toast.error(res.data.message || "Submission failed");
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="bg-white dark:bg-[#18181b] rounded-2xl p-6 text-center shadow-sm border border-gray-100 dark:border-white/[0.06]">
        <div className="w-12 h-12 bg-green-50 dark:bg-green-900/20 rounded-full flex items-center justify-center mx-auto mb-3">
          <CheckCircle2 size={22} className="text-green-600 dark:text-green-400" />
        </div>
        <p className="text-sm font-semibold text-gray-900 dark:text-white mb-1">Proof submitted</p>
        <p className="text-xs text-gray-400 leading-relaxed">
          Admin has been notified and will verify your transfer shortly. Your wallet will be credited once confirmed.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="bg-white dark:bg-[#18181b] rounded-2xl border border-gray-100 dark:border-white/[0.06] shadow-sm overflow-hidden">
        <div className="h-1 w-full bg-gradient-to-r from-green-500 via-emerald-500 to-green-400" />
        <div className="p-5">
          <div className="flex items-center gap-2.5 mb-5">
            <div className="w-8 h-8 bg-green-50 dark:bg-green-900/20 rounded-lg flex items-center justify-center shrink-0">
              <Banknote size={15} className="text-green-600 dark:text-green-400" />
            </div>
            <p className="text-xs font-medium text-gray-900 dark:text-white">Transfer to this account</p>
          </div>

          {loadingDetails ? (
            <div className="flex justify-center py-6">
              <div className="w-6 h-6 rounded-full border-2 border-green-400 border-t-transparent animate-spin" />
            </div>
          ) : (
            <div className="space-y-2">
              <AccountRow label="Bank" value={details?.bankName || "—"} field="mbank" copied={copied} onCopy={copy} />
              <div
                onClick={() => copy(details?.accountNumber, "maccount")}
                className="group flex items-center justify-between bg-gray-50 dark:bg-white/[0.04] hover:bg-green-50 dark:hover:bg-green-900/10 rounded-xl px-4 py-3 cursor-pointer transition"
              >
                <div>
                  <p className="text-[10px] text-gray-400 mb-0.5">Account number</p>
                  <p className="font-mono text-lg font-bold tracking-[0.12em] text-gray-900 dark:text-white leading-none">
                    {details?.accountNumber || "—"}
                  </p>
                </div>
                <div className="w-7 h-7 rounded-lg bg-white dark:bg-white/[0.06] border border-gray-200 dark:border-white/[0.08] flex items-center justify-center shrink-0">
                  {copied === "maccount"
                    ? <CheckCircle2 size={13} className="text-green-500" />
                    : <Copy size={13} className="text-gray-400" />}
                </div>
              </div>
              <AccountRow label="Account name" value={details?.accountName || "—"} field="mname" copied={copied} onCopy={copy} />
            </div>
          )}
        </div>
      </div>

      <form onSubmit={handleSubmit} className="bg-white dark:bg-[#18181b] rounded-2xl border border-gray-100 dark:border-white/[0.06] shadow-sm p-5 space-y-4">
        <p className="text-xs font-medium text-gray-900 dark:text-white">Submit your proof of payment</p>

        <div>
          <label className="block text-[11px] text-gray-400 mb-1.5">Amount transferred (₦) *</label>
          <input
            type="number" min="1" value={amount}
            onChange={(e) => setAmount(e.target.value)}
            placeholder="5000"
            className="w-full px-3 py-2.5 text-sm bg-gray-50 dark:bg-white/[0.04] border border-gray-200 dark:border-white/[0.08] rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500 dark:text-white"
          />
        </div>

        <div>
          <label className="block text-[11px] text-gray-400 mb-1.5">Transaction reference (optional)</label>
          <input
            type="text" value={reference}
            onChange={(e) => setReference(e.target.value)}
            placeholder="e.g. bank's transaction ID"
            className="w-full px-3 py-2.5 text-sm bg-gray-50 dark:bg-white/[0.04] border border-gray-200 dark:border-white/[0.08] rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500 dark:text-white"
          />
        </div>

        <div>
          <label className="block text-[11px] text-gray-400 mb-1.5">Receipt screenshot *</label>
          <label className="flex flex-col items-center justify-center gap-2 border-2 border-dashed border-gray-200 dark:border-white/[0.08] rounded-xl py-6 cursor-pointer hover:border-green-400 transition">
            {receiptPreview ? (
              <img src={receiptPreview} alt="Receipt preview" className="max-h-40 rounded-lg" />
            ) : (
              <>
                <UploadCloud size={22} className="text-gray-400" />
                <span className="text-xs text-gray-400">Tap to upload PNG or JPG</span>
              </>
            )}
            <input type="file" accept="image/png,image/jpeg,image/jpg" onChange={handleFileChange} className="hidden" />
          </label>
        </div>

        <button
          type="submit" disabled={submitting}
          className="w-full py-2.5 text-sm font-medium bg-green-600 hover:bg-green-700 disabled:bg-green-400 text-white rounded-xl transition flex items-center justify-center gap-2"
        >
          {submitting ? (
            <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />Submitting...</>
          ) : (
            "Submit Proof"
          )}
        </button>

        <p className="text-[11px] text-gray-400 text-center leading-relaxed">
          Admin is notified immediately and will credit your wallet after confirming the transfer.
        </p>
      </form>
    </div>
  );
}